import {
  CIMSModule,
  CIMSModuleItem,
  CIMSPageItem,
  pathify,
  resolveValueOrArray,
} from 'shared'
import { SimplePermissions } from 'hooks/useCheckPermission'
import { ItemType } from 'antd/lib/menu/hooks/useItems'
import ProjectLink from 'components/ProjectLink'
import { useAuthContext } from '../../../../context/AuthContext'
import { useGlobalState } from '../../../../context/GlobalStateContext'
import CIMSLink from '../../../../components/CIMSLink'
import { useCallback } from 'react'

export function useProtectedMenuItem({
  pages,
  icon,
  ...rest
}: Module): ItemType {
  const {
    state: { projectGlobalId },
  } = useGlobalState()
  const { user } = useAuthContext()
  const developerModule = rest.module === CIMSModule.Developer

  const authorized = useCallback(
    (permission: Page['permission'] = false) => {
      if (!user) return false
      else if (typeof permission === 'boolean') return permission
      else if (permission === 'developer')
        return user.isFullDeveloper().authorized
      else if (permission === 'admin')
        return user.admin(projectGlobalId).authorized
      return user.someAuthorized(
        resolveValueOrArray(permission),
        projectGlobalId
      ).authorized
    },
    [projectGlobalId, user]
  )

  if (!user) return null
  if (!projectGlobalId && !developerModule) return null

  /**
   * hide the entire "folder" menu item if the user doesn't have access to anything in it
   */
  if (pages.every(({ permission }) => !authorized(permission))) return null
  return {
    label: rest.module.name,
    key: rest.module.id,
    icon,
    children: pages.reduce((pages, { page, icon, permission }) => {
      /**
       * hide sub menu items this user doesn't have access to
       */
      if (!authorized(permission)) return pages

      const label = developerModule ? (
        <CIMSLink to={pathify(rest.module.pathname, page.pathname)}>
          {page.name}
        </CIMSLink>
      ) : (
        <ProjectLink item={page}>{page.name}</ProjectLink>
      )
      pages.push({ key: page.id, label, icon })
      return pages
    }, Array<ItemType>()),
  }
}

type Module = {
  module: CIMSModuleItem
  pages: Page[]
  /**
   * @default <FolderOpenOutlined />
   */
  icon?: React.ReactNode
}

type Page = {
  permission: 'developer' | SimplePermissions
  page: CIMSPageItem
  /**
   * @default null
   */
  icon?: React.ReactNode
}
